'use client';

import type { PropsWithChildren } from 'react';
import React, { createContext, useMemo } from 'react';

export type AnimationVariants =
  | 'mount-mutation'
  | 'exit-mutation'
  | 'mount-virtualization'
  | 'exit-virtualization';

type AnimationDefinition = {
  animated: boolean;
  isFullyMeasured: boolean;
  startY?: number;
  exitY?: number;
  startX?: number;
  exitX?: number;
  type?: AnimationVariants;
};

export const AnimateItemContext = createContext<AnimationDefinition>({
  type: undefined,
  animated: false,
  isFullyMeasured: true,
  startY: undefined,
  exitY: undefined,
  startX: undefined,
  exitX: undefined,
});

export const AnimateItemProvider = ({
  isFullyMeasured,
  animated,
  type,
  children,
  startY,
  exitY,
  startX,
  exitX,
}: PropsWithChildren<AnimationDefinition>) => {
  const context = useMemo(() => {
    return {
      isFullyMeasured,
      animated,
      type,
      startY,
      exitY,
      startX,
      exitX,
    };
  }, [animated, exitX, exitY, isFullyMeasured, startX, startY, type]);

  return (
    <AnimateItemContext.Provider value={context}>
      {children}
    </AnimateItemContext.Provider>
  );
};

export const useAnimateItemContext = () => React.useContext(AnimateItemContext);
